import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  SportsSoccer as SportsSoccerIcon
} from '@mui/icons-material';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';

function PlayersWithoutTeam() {
  const [players, setPlayers] = useState([]);
  const [equipesOptions, setEquipesOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [selectedEquipes, setSelectedEquipes] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [assignedPlayers, setAssignedPlayers] = useState(new Set());
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchInputRef = useRef(null);

  const mediaUrl = getMediaBaseUrl();

  // Fonction pour obtenir l'URL du logo
  const getLogoUrl = (id, type) => {
    return `${mediaUrl}/${type}/${id}.jpg?t=${Date.now()}`;
  };

  // Fonction pour obtenir l'URL de la photo du joueur
  const getPlayerPhotoUrl = (playerId) => {
    // Les photos sont stockées avec l'ID du joueur comme nom de fichier
    return `${mediaUrl}/photos/${playerId}.png?t=${Date.now()}`;
  };
  
  // Fonction pour calculer l'âge à partir de l'année de naissance
  const calculateAge = (anneeNaissance) => {
    if (!anneeNaissance) return '-';
    const currentYear = new Date().getFullYear();
    return currentYear - anneeNaissance;
  };

  useEffect(() => {
    fetchPlayersWithoutTeam();
    fetchCompetitionsStructure();
  }, []);

  // Focus search field when dialog opens
  useEffect(() => {
    if (dialogOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [dialogOpen]);

  const fetchPlayersWithoutTeam = async () => {
    try {
      const response = await api.get('/api/players/without-team');
      setPlayers(response.data.players || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des joueurs:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des joueurs',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const fetchCompetitionsStructure = async () => {
    try {
      const response = await api.get('/api/players/competitions/structure');
      const structure = response.data.structure || [];
      
      // Créer la liste des équipes avec leurs compétitions
      const equipesMap = {};
      structure.forEach(item => {
        if (!equipesMap[item.equipe_id]) {
          equipesMap[item.equipe_id] = {
            equipe_id: item.equipe_id,
            equipe_nom: item.equipe_nom,
            competitions: []
          };
        }
        // Ajouter la compétition si elle n'existe pas déjà
        const exists = equipesMap[item.equipe_id].competitions.find(
          c => c.competition_id === item.competition_id
        );
        if (!exists) {
          equipesMap[item.equipe_id].competitions.push({
            niveau_id: item.niveau_id,
            niveau_nom: item.niveau_nom,
            competition_id: item.competition_id,
            competition_nom: item.competition_nom
          });
        }
      });
      
      // Convertir en array et formater pour l'affichage
      const equipesArray = Object.values(equipesMap).map(equipe => ({
        ...equipe,
        display: `${equipe.equipe_nom} (${equipe.competitions.map(c => c.competition_nom).join(', ')})`
      }));
      
      setEquipesOptions(equipesArray);
    } catch (error) {
      console.error('Erreur lors de la récupération de la structure:', error);
    }
  };

  const handleOpenDialog = (player) => {
    setSelectedPlayer(player);
    setSelectedEquipes([]);
    setSearchTerm('');
    setDialogOpen(true);
    // Focus will be set by useEffect when dialog opens
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPlayer(null);
    setSelectedEquipes([]);
    setSearchTerm('');
  };

  const handleAddEquipe = (equipe) => {
    if (!selectedEquipes.find(e => e.equipe_id === equipe.equipe_id)) {
      setSelectedEquipes([...selectedEquipes, equipe]);
    }
  };

  const handleRemoveEquipe = (equipeId) => {
    setSelectedEquipes(selectedEquipes.filter(e => e.equipe_id !== equipeId));
  };

  const handleAssignTeams = async () => {
    if (!selectedPlayer || selectedEquipes.length === 0) return;

    try {
      const equipeIds = selectedEquipes.map(e => e.equipe_id);
      
      if (equipeIds.length === 1) {
        // Assigner une seule équipe
        await api.post(
          `/api/players/${selectedPlayer.id}/assign-team`,
          { equipeId: equipeIds[0] }
        );
      } else {
        // Assigner plusieurs équipes
        await api.post(
          `/api/players/${selectedPlayer.id}/assign-multiple-teams`,
          { equipeIds }
        );
      }

      // Marquer le joueur comme assigné
      setAssignedPlayers(prev => new Set([...prev, selectedPlayer.id]));
      
      // Retirer le joueur de la liste après un délai
      setTimeout(() => {
        setPlayers(prevPlayers => prevPlayers.filter(p => p.id !== selectedPlayer.id));
      }, 1000);

      setSnackbar({
        open: true,
        message: `Équipe(s) assignée(s) avec succès à ${selectedPlayer.nombre} ${selectedPlayer.apellido}`,
        severity: 'success'
      });

      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'assignation des équipes',
        severity: 'error'
      });
    }
  };

  // Filtrer les équipes selon le terme de recherche
  const getFilteredEquipes = () => {
    if (!searchTerm) return equipesOptions;
    return equipesOptions.filter(equipe => 
      equipe.equipe_nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      equipe.display.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Filtrer les joueurs selon le terme de recherche
  const getFilteredPlayers = () => {
    if (!playerSearchTerm) return players;
    return players.filter(player => 
      (player.nom && player.nom.toLowerCase().includes(playerSearchTerm.toLowerCase())) ||
      (player.nationalite && player.nationalite.toLowerCase().includes(playerSearchTerm.toLowerCase())) ||
      (player.poste && player.poste.toLowerCase().includes(playerSearchTerm.toLowerCase())) ||
      (player.team && player.team.toLowerCase().includes(playerSearchTerm.toLowerCase()))
    );
  };

  const formatPosition = (position) => {
    if (!position) return '';
    return position.split(',').map(pos => (
      <Chip 
        key={pos} 
        label={pos.trim()} 
        size="small" 
        sx={{ mr: 0.5, mb: 0.5 }}
        color="primary"
        variant="outlined"
      />
    ));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredPlayers = getFilteredPlayers();

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SportsSoccerIcon sx={{ mr: 2 }} />
          Assignation Rapide des Équipes
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {players.length} joueur(s) sans équipe assignée dans la table comp_player_equipes
        </Typography>
        
        {/* Barre de recherche pour les joueurs */}
        <TextField
          fullWidth
          placeholder="Rechercher un joueur par nom, nationalité ou position..."
          variant="outlined"
          value={playerSearchTerm}
          onChange={(e) => setPlayerSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ mb: 3 }}
        />

        {/* Statistiques */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Joueurs
                </Typography>
                <Typography variant="h4">
                  {players.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Assignés (cette session)
                </Typography>
                <Typography variant="h4" color="success.main">
                  {assignedPlayers.size}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Restants
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {filteredPlayers.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Liste des joueurs */}
      {isMobile ? (
        // Vue mobile : Cards
        <Grid container spacing={2}>
          {filteredPlayers.map((player) => (
            <Grid item xs={12} key={player.id}>
              <Card 
                sx={{ 
                  opacity: assignedPlayers.has(player.id) ? 0.5 : 1,
                  transition: 'opacity 0.3s',
                  position: 'relative'
                }}
              >
                {assignedPlayers.has(player.id) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'success.main',
                      opacity: 0.1,
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 60, color: 'success.main', opacity: 0.5 }} />
                  </Box>
                )}
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={getPlayerPhotoUrl(player.id)}
                      sx={{ width: 56, height: 56, mr: 2 }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">
                        {player.nom}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {player.nationalite} • {calculateAge(player.annee_naissance)} ans
                      </Typography>
                      {player.team && (
                        <Typography variant="body2" color="text.secondary">
                          {player.team}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    {formatPosition(player.poste)}
                  </Box>
                  <Typography variant="body2">
                    {player.taille && `${player.taille}cm`}
                    {player.poids && player.poids !== 'null' && ` • ${player.poids}kg`}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog(player)}
                    disabled={assignedPlayers.has(player.id)}
                  >
                    Assigner Équipe
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Vue desktop : Table
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Photo</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Nationalité</TableCell>
                <TableCell>Âge</TableCell>
                <TableCell>Équipe actuelle</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlayers.map((player) => (
                <TableRow 
                  key={player.id}
                  onClick={() => !assignedPlayers.has(player.id) && handleOpenDialog(player)}
                  sx={{ 
                    opacity: assignedPlayers.has(player.id) ? 0.5 : 1,
                    backgroundColor: assignedPlayers.has(player.id) ? 'success.light' : 'inherit',
                    transition: 'all 0.3s',
                    cursor: assignedPlayers.has(player.id) ? 'default' : 'pointer',
                    '&:hover': {
                      backgroundColor: assignedPlayers.has(player.id) ? 'success.light' : 'action.hover'
                    }
                  }}
                >
                  <TableCell>
                    <Avatar
                      src={getPlayerPhotoUrl(player.id)}
                      sx={{ width: 40, height: 40 }}
                    >
                      <PersonIcon />
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {player.nom}
                    </Typography>
                    {assignedPlayers.has(player.id) && (
                      <Chip 
                        label="Assigné" 
                        size="small" 
                        color="success" 
                        icon={<CheckIcon />}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{formatPosition(player.poste)}</TableCell>
                  <TableCell>{player.nationalite}</TableCell>
                  <TableCell>{calculateAge(player.annee_naissance)}</TableCell>
                  <TableCell>{player.team || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Assigner une équipe">
                      <IconButton
                        color="primary"
                        disabled={assignedPlayers.has(player.id)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog pour sélectionner les équipes */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                src={selectedPlayer && getPlayerPhotoUrl(selectedPlayer.id)}
                sx={{ width: 40, height: 40, mr: 2 }}
              >
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h6">
                  Assigner une équipe
                </Typography>
                {selectedPlayer && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedPlayer.nom} • {selectedPlayer.poste}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <TextField
            fullWidth
            inputRef={searchInputRef}
            autoFocus
            placeholder="Rechercher une équipe..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          {/* Équipes sélectionnées */}
          {selectedEquipes.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Équipes sélectionnées ({selectedEquipes.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedEquipes.map((equipe) => (
                  <Chip
                    key={equipe.equipe_id}
                    label={equipe.equipe_nom}
                    onDelete={() => handleRemoveEquipe(equipe.equipe_id)}
                    color="primary"
                    avatar={
                      <Avatar 
                        src={getLogoUrl(equipe.equipe_id, 'equipes')}
                        sx={{ width: 24, height: 24 }}
                      />
                    }
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Liste des équipes disponibles */}
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {getFilteredEquipes().map((equipe) => {
              const isSelected = selectedEquipes.find(e => e.equipe_id === equipe.equipe_id);
              
              return (
                <ListItem
                  key={equipe.equipe_id}
                  button
                  onClick={() => !isSelected && handleAddEquipe(equipe)}
                  disabled={isSelected}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: isSelected ? 'action.selected' : 'inherit'
                  }}
                >
                  <Avatar
                    src={getLogoUrl(equipe.equipe_id, 'equipes')}
                    sx={{ width: 40, height: 40, mr: 2 }}
                  />
                  <ListItemText
                    primary={equipe.equipe_nom}
                    secondary={equipe.competitions.map(c => c.competition_nom).join(', ')}
                  />
                  {isSelected && (
                    <CheckIcon color="success" />
                  )}
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Annuler
          </Button>
          <Button 
            onClick={handleAssignTeams}
            variant="contained"
            disabled={selectedEquipes.length === 0}
            startIcon={<CheckIcon />}
          >
            Assigner ({selectedEquipes.length} équipe{selectedEquipes.length > 1 ? 's' : ''})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PlayersWithoutTeam;